import { reportUiFailure } from "$lib/report-failure.js";

// Live timeline frames per agent, applied together once per animation frame. rAF stops in a
// hidden tab, so a timer backs it up. Anything else about an agent flushes that agent first.
const HIDDEN_TAB_FALLBACK_MS = 100;

export interface FrameBatchDeps<F> {
  apply(frame: F): void;
  // Runs one flush: every store change inside it notifies its readers once.
  batch(run: () => void): void;
}

export class AgentFrameBatch<F> {
  private queues = new Map<string, F[]>();
  private resets = 0;
  private animationFrame: number | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly deps: FrameBatchDeps<F>) {}

  enqueue(agentId: string, frame: F): void {
    let queue = this.queues.get(agentId);
    if (!queue) this.queues.set(agentId, (queue = []));
    queue.push(frame);
    this.schedule();
  }

  flushAgent(agentId: string): void {
    const queue = this.queues.get(agentId);
    if (!queue) return;
    this.queues.delete(agentId);
    if (this.queues.size === 0) this.cancelSchedule();
    this.deps.batch(() => this.applyQueue(agentId, queue));
  }

  flush(): void {
    this.cancelSchedule();
    const queues = this.queues;
    if (queues.size === 0) return;
    // A frame an apply holds and replays joins the next batch, behind this one.
    this.queues = new Map();
    this.deps.batch(() => {
      for (const [agentId, queue] of queues) this.applyQueue(agentId, queue);
    });
  }

  // A sign-out: what was queued belongs to the conversations it drops.
  reset(): void {
    this.cancelSchedule();
    this.queues.clear();
    this.resets += 1;
  }

  size(): number {
    return this.queues.size;
  }

  private applyQueue(agentId: string, queue: readonly F[]): void {
    const resets = this.resets;
    for (const frame of queue) {
      if (this.resets !== resets) return; // signed out mid-flush
      try {
        this.deps.apply(frame);
      } catch (err) {
        // One bad frame must not take the agent's and the other agents' queued frames with it.
        reportUiFailure("agents.stream_frame_apply", err, { agentId });
      }
    }
  }

  private schedule(): void {
    if (this.timer !== undefined) return;
    this.timer = setTimeout(() => this.flush(), HIDDEN_TAB_FALLBACK_MS);
    if (typeof requestAnimationFrame === "function") {
      this.animationFrame = requestAnimationFrame(() => this.flush());
    }
  }

  private cancelSchedule(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
    if (this.animationFrame !== undefined) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = undefined;
  }
}
